/**
 * CreditBalance component
 * Per-Use Billing: Task 7.5
 * Refactored: Uses BillingContext for shared state with caching
 *
 * Displays current prepaid credit balance.
 * Shown when FF_BILLING_PREPAID or FF_BILLING_ADMIN_TOP_UP_ONLY is enabled.
 */

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useBilling } from "@/contexts/useBilling";
import { useFeatureFlag } from "@/services/feature-flags";
import {
  formatBalance,
  formatDateTimePl,
  LOW_BALANCE_THRESHOLD,
} from "@/features/billing/billing-format";

function CreditIcon({ isLowBalance, balanceColor }: {
  isLowBalance: boolean;
  balanceColor: string;
}) {
  return (
    <div
      className={`flex h-10 w-10 items-center justify-center rounded-full ${
        isLowBalance
          ? "bg-[color:color-mix(in_srgb,var(--risk-medium)_18%,var(--card))]"
          : "bg-[color:color-mix(in_srgb,var(--risk-low)_18%,var(--card))]"
      }`}
    >
      <svg
        className={`h-5 w-5 ${balanceColor}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </div>
  );
}

function LowBalanceWarning() {
  const { t } = useTranslation(["billing"]);
  return (
    <div className="flex items-center gap-1 text-warning">
      <svg
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <span className="text-xs font-medium">
        {t("billing:creditBalance.lowBalance")}
      </span>
    </div>
  );
}

function CreditBalanceSkeleton() {
  return (
    <div
      className="rounded-lg border border-border bg-card p-4"
      data-test="credit-balance-loading"
    >
      <div className="animate-pulse flex items-center gap-3">
        <div className="h-8 w-8 bg-muted rounded-full"></div>
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-24"></div>
          <div className="h-3 bg-muted rounded w-16"></div>
        </div>
      </div>
    </div>
  );
}

function CreditBalanceError({ error, onRetry }: {
  error: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation(["billing"]);
  return (
    <div
      className="rounded-lg border border-[color:color-mix(in_srgb,var(--risk-high)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--risk-high)_16%,var(--card))] p-4"
      data-test="credit-balance-error"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg
            className="h-5 w-5 text-destructive"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm text-destructive">{error}</span>
        </div>
        <Button
          variant="link"
          size="sm"
          onClick={onRetry}
          data-test="credit-balance-retry"
        >
          {t("billing:retry")}
        </Button>
      </div>
    </div>
  );
}

export default function CreditBalance() {
  const { t } = useTranslation(["billing"]);
  const { credits, creditsLoadingState, creditsError, refreshCredits } =
    useBilling();

  const isPrepaidEnabled = useFeatureFlag("BILLING_PREPAID");
  const isAdminTopUpMode = useFeatureFlag("BILLING_ADMIN_TOP_UP_ONLY");
  const showTestItem = useFeatureFlag("TEST_ITEM_STRIPE");
  const shouldShowCredits = isPrepaidEnabled || isAdminTopUpMode || showTestItem;

  if (!shouldShowCredits) {
    return null;
  }

  if (creditsLoadingState === "loading" || creditsLoadingState === "idle") {
    return <CreditBalanceSkeleton />;
  }

  if (creditsLoadingState === "error" && creditsError) {
    // force=true: the error path armed the TTL gate, so a plain refresh()
    // would be a no-op for up to 30 s and the error panel would stay up.
    return (
      <CreditBalanceError error={creditsError} onRetry={() => void refreshCredits(true)} />
    );
  }

  if (!credits) {
    return null;
  }

  const isLowBalance = credits.balance < LOW_BALANCE_THRESHOLD;
  const balanceColor = isLowBalance ? "text-warning" : "text-success";
  const borderColor = isLowBalance
    ? "border-[color:color-mix(in_srgb,var(--risk-medium)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--risk-medium)_18%,var(--card))]"
    : "border-[color:color-mix(in_srgb,var(--risk-low)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--risk-low)_16%,var(--card))]";

  return (
    <div
      className={`rounded-lg border p-4 ${borderColor}`}
      data-test="credit-balance"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CreditIcon isLowBalance={isLowBalance} balanceColor={balanceColor} />
          <div>
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${balanceColor}`}>
                {formatBalance(credits.balance)}
              </span>
              <span className="text-sm text-muted-foreground">
                {t("billing:creditBalance.credits")}
              </span>
            </div>
            {credits.lastUpdated && (
              <p className="text-xs text-muted-foreground">
                {t("billing:creditBalance.lastUpdated")}:{" "}
                {formatDateTimePl(credits.lastUpdated)}
              </p>
            )}
          </div>
        </div>
        {isLowBalance && <LowBalanceWarning />}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        {t("billing:creditBalance.explanation")}
      </p>
    </div>
  );
}
